import { useState, useRef, useEffect, useCallback } from 'react';
import { Attachment } from '@/types';
import { toast } from "@/components/ui/use-toast";
import { useChatCommands } from './useChatCommands';
import { createAttachment, readFileContent } from '@/utils/chatUtils';
import { useKnowledgeBases } from '@/hooks/useKnowledgeBases';

export function useChatInput(
  onSendMessage: (message: string, attachments?: Attachment[], kbId?: string | null, kbName?: string | null) => void,
  onClearChat?: () => void,
  onOpenCodeEditor?: () => void,
  onOpenCanvas?: () => void,
  onExportChat?: () => void,
  isLoading: boolean = false,
  chatId?: string
) {
  const [message, setMessage] = useState('');
  const [pendingAttachments, setPendingAttachments] = useState<Attachment[]>([]);
  const [isRecording, setIsRecording] = useState(false);
  const [responseMode, setResponseMode] = useState<'normal' | 'code'>('normal');
  const [activeKnowledgeBase, setActiveKnowledgeBase] = useState<string | null>(null);
  const [activeKnowledgeBaseId, setActiveKnowledgeBaseId] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [recognition, setRecognition] = useState<SpeechRecognition | null>(null);

  const [messageHistory, setMessageHistory] = useState<string[]>([]);
  const [historyIndex, setHistoryIndex] = useState<number>(-1);
  const [storedCurrentMessage, setStoredCurrentMessage] = useState<string>('');

  const storageKey = chatId ? `chat_history_${chatId}` : null;

  const { knowledgeBases } = useKnowledgeBases();

  useEffect(() => {
    if (storageKey) {
      const savedHistory = localStorage.getItem(storageKey);
      if (savedHistory) {
        try {
          const parsedHistory = JSON.parse(savedHistory);
          if (Array.isArray(parsedHistory)) {
            setMessageHistory(parsedHistory);
          }
        } catch (e) {
          console.error('Error parsing message history:', e);
        }
      }
    }
  }, [storageKey]);

  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

  const commands = useChatCommands(
    message, 
    onClearChat, 
    onOpenCodeEditor, 
    onExportChat, 
    (kbName) => { /* This is now handled by handleSelectKnowledgeBase */ },
    setMessage
  );

  useEffect(() => {
    if (typeof window !== 'undefined' && 'webkitSpeechRecognition' in window) {
      const SpeechRecognition = window.webkitSpeechRecognition;
      const recognitionInstance = new SpeechRecognition();
      recognitionInstance.continuous = true;
      recognitionInstance.interimResults = true;
      
      recognitionInstance.onresult = (event) => {
        const transcript = Array.from(event.results)
          .map(result => result[0])
          .map(result => result.transcript)
          .join('');
        
        setMessage(transcript);
        if (textareaRef.current) {
          textareaRef.current.style.height = 'auto';
          textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
        }
      };
      
      recognitionInstance.onend = () => {
        setIsRecording(false);
      };
      
      setRecognition(recognitionInstance);
    }
  }, []);

  const saveToHistory = useCallback((msg: string) => {
    if (msg.trim() && storageKey) {
      setMessageHistory(prev => {
        if (prev.length > 0 && prev[prev.length - 1] === msg) {
          return prev;
        }
        
        const newHistory = [...prev.filter(m => m !== msg), msg];
        
        const limitedHistory = newHistory.slice(-50);
        
        localStorage.setItem(storageKey, JSON.stringify(limitedHistory));
        
        return limitedHistory;
      });
    }
  }, [storageKey]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
  
    if (message.trim().startsWith('/') && commands.isCommandMode) {
      const commandExecuted = commands.executeCommand(message);
      if (commandExecuted) {
        return;
      }
    }
  
    if ((message.trim() || pendingAttachments.length > 0) && !isLoading) {
      let finalMessage = message.trim();
      let kbIdForSend = activeKnowledgeBaseId;
      let kbNameForSend = activeKnowledgeBase;

      const kbCommandMatch = finalMessage.match(/^\/kb\s+([^\s]+)(\s+.+)?/);
      if (kbCommandMatch && !kbIdForSend) {
        const kbIdentifier = kbCommandMatch[1];
        const restOfMessage = kbCommandMatch[2] || '';
        finalMessage = restOfMessage.trim();
        
        const matchedKb = knowledgeBases.find(
          kb => kb.name.toLowerCase() === kbIdentifier.toLowerCase() || kb.id === kbIdentifier
        );
        if (matchedKb) {
          kbIdForSend = matchedKb.id;
          kbNameForSend = matchedKb.name;
        }
      }

      if (responseMode === 'code' && !finalMessage.includes('/code')) {
        finalMessage = `/code ${finalMessage}`;
      }
  
      saveToHistory(message);
      setHistoryIndex(-1);
  
      onSendMessage(finalMessage, pendingAttachments, kbIdForSend, kbNameForSend);
      
      setMessage('');
      setPendingAttachments([]);
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
    }
  };
  
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
      return;
    }
    
    if (historyIndex === -1 && e.key === 'ArrowUp' && message.trim()) {
      setStoredCurrentMessage(message);
    }
    
    if (e.key === 'ArrowUp' && !e.shiftKey) {
      e.preventDefault();
      if (messageHistory.length > 0) {
        const newIndex = historyIndex + 1 >= messageHistory.length 
          ? messageHistory.length - 1 
          : historyIndex + 1;
        setHistoryIndex(newIndex);
        setMessage(messageHistory[messageHistory.length - 1 - newIndex]);
        setTimeout(() => {
          if (textareaRef.current) {
            textareaRef.current.selectionStart = textareaRef.current.value.length;
            textareaRef.current.selectionEnd = textareaRef.current.value.length;
          }
        }, 0);
      }
    } else if (e.key === 'ArrowDown' && !e.shiftKey && historyIndex >= 0) {
      e.preventDefault();
      if (historyIndex === 0) {
        setMessage(storedCurrentMessage);
        setHistoryIndex(-1);
        setStoredCurrentMessage('');
      } else {
        const newIndex = historyIndex - 1;
        setHistoryIndex(newIndex);
        setMessage(messageHistory[messageHistory.length - 1 - newIndex]);
      }
      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.selectionStart = textareaRef.current.value.length;
          textareaRef.current.selectionEnd = textareaRef.current.value.length;
        }
      }, 0);
    }
  };

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setMessage(e.target.value);
    e.target.style.height = 'auto';
    e.target.style.height = `${e.target.scrollHeight}px`;
  };

  const toggleRecording = () => {
    if (!recognition) return;
    
    if (isRecording) {
      recognition.stop();
      setIsRecording(false);
    } else {
      recognition.start();
      setIsRecording(true);
    }
  };

  const handleFileSelect = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast({
          title: "File too large",
          description: "Maximum file size is 10 MB",
          variant: "destructive"
        });
        return;
      }
      
      const isImage = file.type.startsWith('image/');
      const isDocument = file.type === 'application/pdf' || 
                      file.type === 'text/plain' || 
                      file.type === 'text/csv' || 
                      file.name.endsWith('.pdf') || 
                      file.name.endsWith('.txt') || 
                      file.name.endsWith('.csv') || 
                      file.name.endsWith('.docx');
      
      if (!isImage && !isDocument) {
        toast({
          title: "Unsupported file type",
          description: "Please upload an image, PDF, TXT, CSV, or DOCX file",
          variant: "destructive"
        });
        return;
      }
      
      let content: string | null = null;
      if (file.type === 'application/pdf' || file.name.endsWith('.pdf')) {
        content = await readFileContent(file);
        if (!content || content.trim().length === 0) {
          content = `[This is a PDF file: ${file.name}]\n\nUnable to extract text content from this PDF.`;
        }
      } else if (isDocument) {
        content = await readFileContent(file);
      } else {
        const reader = new FileReader();
        content = await new Promise<string>((resolve, reject) => {
          reader.onload = (event) => {
            if (event.target?.result) {
              resolve(event.target.result as string);
            } else {
              reject(new Error('Failed to read file'));
            }
          };
          reader.onerror = (error) => reject(error);
          reader.readAsDataURL(file);
        });
      }

      if (file.type === 'application/pdf' || file.name.endsWith('.pdf') || isDocument) {
        const newAttachment: Attachment = {
          id: Math.random().toString(36).substring(2, 11),
          type: 'file',
          content: content || '',
          name: file.name
        };
        setPendingAttachments(prev => [...prev, newAttachment]);
      } else {
        const newAttachment: Attachment = {
          id: Math.random().toString(36).substring(2, 11),
          type: isImage ? 'image' : 'file',
          content: content || '',
          name: file.name
        };
        setPendingAttachments(prev => [...prev, newAttachment]);
      }
    }
  };
  
  const handleRemoveAttachment = (id: string) => {
    setPendingAttachments(prev => prev.filter(attachment => attachment.id !== id));
  };

  const handleModeChange = (value: string) => {
    if (value) {
      setResponseMode(value as 'normal' | 'code');
      toast({
        title: `${value === 'code' ? 'Code-optimized' : 'Normal'} mode activated`,
        description: `AI responses will be ${value === 'code' ? 'formatted for code' : 'conversational'}`
      });
    }
  };

  const handleSelectKnowledgeBase = (kbId: string, kbName: string) => {
    setActiveKnowledgeBase(kbName);
    setActiveKnowledgeBaseId(kbId);
    setMessage('');
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  };
  
  const clearKnowledgeBase = () => {
    setActiveKnowledgeBase(null);
    setActiveKnowledgeBaseId(null);
    setMessage('');
  };

  return {
    message,
    pendingAttachments,
    setPendingAttachments,
    isRecording,
    responseMode,
    activeKnowledgeBase,
    activeKnowledgeBaseId,
    textareaRef,
    fileInputRef,
    handleSubmit,
    handleKeyDown,
    handleTextareaChange,
    toggleRecording,
    handleFileSelect,
    handleFileChange,
    handleRemoveAttachment,
    handleModeChange,
    handleSelectKnowledgeBase,
    clearKnowledgeBase,
    isCommandMode: commands.isCommandMode,
    currentCommand: commands.currentCommand,
    messageHistory,
    setMessage,
  };
}